import { FC, useState, useEffect } from "react";
import {
    IonCard,
    IonContent,
    IonHeader,
    IonIcon,
    IonInput,
    IonLabel,
    IonLoading,
    IonModal,
    IonPage,
    IonSegmentButton,
    IonTitle,
    IonToolbar,
    IonButtons,
    IonButton,
    useIonToast,
} from "@ionic/react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router";
import { FormProvider, useForm, useWatch, Controller } from "react-hook-form";
import { useIonRouter } from "@ionic/react";
import {
    shirtOutline,
    shirt,
    closeOutline,
} from "ionicons/icons";
import locationPitchIcon from "../../assets/images/icons/location-pitch.svg";
import venueStadiumIcon from "../../assets/images/icons/venue-stadium.svg";
import competitionTrophyIcon from "../../assets/images/icons/competition-trophy.svg";
import positionPlayerIcon from "../../assets/images/icons/position-player.svg";
import {
    EventName,
    GoalEvent,
    MatchEvent,
    MatchLocation,
    MatchPhone,
    SelectedTeam,
} from "refsix-js-models";
import { makeShortName, updateTeamNameInEvents } from "refsix-core";
import { values, filter, orderBy, max, keys, cloneDeep } from "lodash";

import { HeaderComponent, ColorPickerComponent } from "../../components";
import { useMatch } from "../../components/hooks/match";
import { useProfile } from "../../components/hooks";
import { recalculateStats, updateMatch } from "../../services/matchService";
import { routes } from "../../route/constants";
import { ScoreCounter } from "../../components/ScoreCounter";
import { PositionDropdown } from "../../components/PositionDropdown";
import { IonFormSegment } from "../../components/r6ionic/R6IonSegment";
import "./EditMatchDetails.css";

interface EditMatchForm {
    myTeamName: string;
    myTeamShort: string;
    myTeamColor: string;
    opponentName: string;
    opponentShort: string;
    opponentColor: string;
    myTeamScore: number;
    opponentScore: number;
    location: MatchLocation;
    venue: string;
    competition: string;
    playerPosition: string;
}

interface EditMatchDetailsProps {
    match: { params: { id: string } };
}

const EditMatchDetails: FC<EditMatchDetailsProps> = ({ match: routeMatch }) => {
    const { t } = useTranslation();
    const { id } = useParams<{ id: string }>();
    const router = useIonRouter();
    const profile = useProfile();
    const matchPhone = useMatch(id);

    const [loading, setLoading] = useState(!matchPhone);
    const [showColorPicker, setShowColorPicker] = useState(false);
    const [colorPickerTeam, setColorPickerTeam] = useState<MatchLocation>(MatchLocation.home);

    // Determine initial match location based on existing data or default to home
    const initialMatchLocation: MatchLocation = matchPhone?.matchLocation || MatchLocation.home;

    // Determine which team is "my team" based on matchLocation
    const getTeamMapping = (location: MatchLocation) => {
        if (location === MatchLocation.home) {
            return {
                myTeam: {
                    name: matchPhone?.homeTeam || "",
                    short: matchPhone?.homeTeamShort || "",
                    color: matchPhone?.homeColor || "",
                },
                opponent: {
                    name: matchPhone?.awayTeam || "",
                    short: matchPhone?.awayTeamShort || "",
                    color: matchPhone?.awayColor || "",
                },
            };
        } else {
            return {
                myTeam: {
                    name: matchPhone?.awayTeam || "",
                    short: matchPhone?.awayTeamShort || "",
                    color: matchPhone?.awayColor || "",
                },
                opponent: {
                    name: matchPhone?.homeTeam || "",
                    short: matchPhone?.homeTeamShort || "",
                    color: matchPhone?.homeColor || "",
                },
            };
        }
    };

    const initialMapping = getTeamMapping(initialMatchLocation);

    // Count goals for a specific team from events, with own-goal flipping
    const countGoalsForSide = (
        events: { [key: string]: MatchEvent } | undefined,
        side: SelectedTeam
    ): number => {
        if (!events) return 0;
        return filter(values(events), (event) => {
            if (event.eventName !== EventName.goal) return false;
            const goalEvent = event as GoalEvent;
            if (goalEvent.ownGoal) {
                return goalEvent.team?.side !== side;
            }
            return goalEvent.team?.side === side;
        }).length;
    };

    // Get scores by counting goal events directly (consistent with updateScoreEvents)
    const getScores = (location: MatchLocation) => {
        const homeScore = countGoalsForSide(matchPhone?.matchEvents, SelectedTeam.home);
        const awayScore = countGoalsForSide(matchPhone?.matchEvents, SelectedTeam.away);
        if (location === MatchLocation.home) {
            return { myScore: homeScore, opponentScore: awayScore };
        } else {
            return { myScore: awayScore, opponentScore: homeScore };
        }
    };

    const initialScores = getScores(initialMatchLocation);

    const formMethods = useForm<EditMatchForm>({
        defaultValues: {
            myTeamName: initialMapping.myTeam.name,
            myTeamShort: initialMapping.myTeam.short,
            myTeamColor: initialMapping.myTeam.color,
            opponentName: initialMapping.opponent.name,
            opponentShort: initialMapping.opponent.short,
            opponentColor: initialMapping.opponent.color,
            myTeamScore: initialScores.myScore,
            opponentScore: initialScores.opponentScore,
            location: initialMatchLocation,
            venue: matchPhone?.venue || "",
            competition: matchPhone?.competition || "",
            playerPosition: matchPhone?.playerPosition || "",
        },
    });

    const { handleSubmit, setValue, watch, formState, reset } = formMethods;
    const [presentToast] = useIonToast();

    // Watch for form changes
    const location = useWatch({ control: formMethods.control, name: "location" });
    const myTeamName = useWatch({ control: formMethods.control, name: "myTeamName" });

    // Update default values when match loads
    useEffect(() => {
        if (matchPhone && loading) {
            const loc = matchPhone.matchLocation || MatchLocation.home;
            const mapping = getTeamMapping(loc);
            const scores = getScores(loc);
            reset({
                myTeamName: mapping.myTeam.name,
                myTeamShort: mapping.myTeam.short,
                myTeamColor: mapping.myTeam.color,
                opponentName: mapping.opponent.name,
                opponentShort: mapping.opponent.short,
                opponentColor: mapping.opponent.color,
                myTeamScore: scores.myScore,
                opponentScore: scores.opponentScore,
                location: loc,
                venue: matchPhone.venue || "",
                competition: matchPhone.competition || "",
                playerPosition: matchPhone.playerPosition || "",
            });
            setLoading(false);
        }
    }, [matchPhone, loading, reset]);

    // Auto-generate short name when team name changes
    useEffect(() => {
        const { dirtyFields } = formState;
        if (myTeamName && dirtyFields.myTeamName && !dirtyFields.myTeamShort) {
            setValue("myTeamShort", makeShortName(myTeamName));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [myTeamName, setValue]);

    const opponentName = useWatch({ control: formMethods.control, name: "opponentName" });
    useEffect(() => {
        const { dirtyFields } = formState;
        if (opponentName && dirtyFields.opponentName && !dirtyFields.opponentShort) {
            setValue("opponentShort", makeShortName(opponentName));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [opponentName, setValue]);

    // Watch team colors
    const myTeamColor = watch("myTeamColor");
    const opponentColor = watch("opponentColor");

    // Color picker handlers
    const openColorPicker = (team: MatchLocation) => {
        setColorPickerTeam(team);
        setShowColorPicker(true);
    };

    const handleColorPicked = (color: string) => {
        if (colorPickerTeam === MatchLocation.home) {
            setValue("myTeamColor", color);
        } else {
            setValue("opponentColor", color);
        }
        setShowColorPicker(false);
    };

    // Alias for use inside updateScoreEvents
    const countGoalsForTeam = countGoalsForSide;

    // Update score by adding/removing goal events
    const updateScoreEvents = (
        matchPhone: MatchPhone,
        matchLocation: MatchLocation,
        data: EditMatchForm
    ): { [key: string]: MatchEvent } => {
        const events = { ...matchPhone.matchEvents } || {};

        // Determine which side corresponds to home/away
        const homeSide = SelectedTeam.home;
        const awaySide = SelectedTeam.away;

        const currentHomeGoals = countGoalsForTeam(events, homeSide);
        const currentAwayGoals = countGoalsForTeam(events, awaySide);

        // Calculate target goals based on match location
        let targetHomeGoals: number;
        let targetAwayGoals: number;
        if (matchLocation === MatchLocation.home) {
            targetHomeGoals = data.myTeamScore;
            targetAwayGoals = data.opponentScore;
        } else {
            targetHomeGoals = data.opponentScore;
            targetAwayGoals = data.myTeamScore;
        }

        // Get max timestamp to add new events after all existing ones
        const eventKeys = keys(events).map((key) => parseInt(key));
        let maxTs = eventKeys.length > 0 ? (max(eventKeys) || 0) + 1 : 1;

        // Helper to add goals
        const addGoals = (side: SelectedTeam, count: number, teamName: string, teamShort: string) => {
            for (let i = 0; i < count; i++) {
                events[maxTs.toString()] = {
                    timestamp: maxTs,
                    eventName: EventName.goal,
                    ownGoal: false,
                    penalty: false,
                    freeKick: false,
                    minuteOfPlay: 1,
                    half: 1,
                    player: {
                        name: t("fixture.FixtureTeam.unknownPlayer"),
                        number: 1,
                        captain: false,
                        starting: true,
                    },
                    team: {
                        side,
                        teamName,
                        shortName: teamShort,
                    },
                } as GoalEvent;
                maxTs++;
            }
        };

        // Helper to remove goals that count toward a team's score
        // This includes regular goals by that team AND own goals by the opposite team
        const removeGoals = (side: SelectedTeam, count: number) => {
            // Build entries with their actual map keys to handle key/timestamp mismatches
            const goalEntries = orderBy(
                Object.entries(events)
                    .filter(([, event]) => {
                        if (event.eventName !== EventName.goal) return false;
                        const goalEvent = event as GoalEvent;
                        if (goalEvent.ownGoal) {
                            return goalEvent.team?.side !== side;
                        }
                        return goalEvent.team?.side === side;
                    })
                    .map(([key, event]) => ({ key, event })),
                [(entry) => entry.event.timestamp],
                ["desc"]
            );

            for (let i = 0; i < count && i < goalEntries.length; i++) {
                delete events[goalEntries[i].key];
            }
        };

        // Resolve team names from submitted form data
        const homeTeamName = matchLocation === MatchLocation.home ? data.myTeamName : data.opponentName;
        const homeTeamShort = matchLocation === MatchLocation.home ? data.myTeamShort : data.opponentShort;
        const awayTeamName = matchLocation === MatchLocation.away ? data.myTeamName : data.opponentName;
        const awayTeamShort = matchLocation === MatchLocation.away ? data.myTeamShort : data.opponentShort;

        // Adjust home goals
        if (targetHomeGoals > currentHomeGoals) {
            addGoals(homeSide, targetHomeGoals - currentHomeGoals, homeTeamName, homeTeamShort);
        } else if (targetHomeGoals < currentHomeGoals) {
            removeGoals(homeSide, currentHomeGoals - targetHomeGoals);
        }

        // Adjust away goals
        if (targetAwayGoals > currentAwayGoals) {
            addGoals(awaySide, targetAwayGoals - currentAwayGoals, awayTeamName, awayTeamShort);
        } else if (targetAwayGoals < currentAwayGoals) {
            removeGoals(awaySide, currentAwayGoals - targetAwayGoals);
        }

        return events;
    };

    const onSubmit = async (data: EditMatchForm) => {
        if (!matchPhone) return;

        // Map form data back to home/away based on match location
        const matchLocation = data.location;

        let updatedMatch: MatchPhone;
        if (matchLocation === MatchLocation.home) {
            updatedMatch = {
                ...matchPhone,
                homeTeam: data.myTeamName,
                homeTeamShort: data.myTeamShort || makeShortName(data.myTeamName),
                homeColor: data.myTeamColor,
                awayTeam: data.opponentName,
                awayTeamShort: data.opponentShort || makeShortName(data.opponentName),
                awayColor: data.opponentColor,
                venue: data.venue,
                competition: data.competition,
                matchLocation: MatchLocation.home,
                playerPosition: data.playerPosition,
            };
        } else {
            updatedMatch = {
                ...matchPhone,
                awayTeam: data.myTeamName,
                awayTeamShort: data.myTeamShort || makeShortName(data.myTeamName),
                awayColor: data.myTeamColor,
                homeTeam: data.opponentName,
                homeTeamShort: data.opponentShort || makeShortName(data.opponentName),
                homeColor: data.opponentColor,
                venue: data.venue,
                competition: data.competition,
                matchLocation: MatchLocation.away,
                playerPosition: data.playerPosition,
            };
        }

        // Deep clone matchEvents to avoid mutating Redux/store state
        updatedMatch.matchEvents = cloneDeep(matchPhone.matchEvents) || {};

        // Detect location swap
        const originalLocation = matchPhone.matchLocation || MatchLocation.home;
        const locationSwapped = originalLocation !== matchLocation;

        // When location is swapped, swap event.team.side so it tracks the actual team
        if (locationSwapped && updatedMatch.matchEvents) {
            for (const key of Object.keys(updatedMatch.matchEvents)) {
                const event: any = updatedMatch.matchEvents[key];
                if (event.team?.side === SelectedTeam.home) {
                    event.team.side = SelectedTeam.away;
                } else if (event.team?.side === SelectedTeam.away) {
                    event.team.side = SelectedTeam.home;
                }
            }
        }

        // Update team names in the cloned events
        updateTeamNameInEvents(
            updatedMatch,
            matchPhone.homeTeam,
            matchPhone.awayTeam,
            updatedMatch.homeTeam,
            updatedMatch.homeTeamShort,
            updatedMatch.awayTeam,
            updatedMatch.awayTeamShort
        );

        // Move existing players onto new team names, following the team through location swaps
        const originalHomePlayers = cloneDeep(matchPhone.players?.[matchPhone.homeTeam] || []);
        const originalAwayPlayers = cloneDeep(matchPhone.players?.[matchPhone.awayTeam] || []);
        updatedMatch.players = {
            [updatedMatch.homeTeam]: locationSwapped ? originalAwayPlayers : originalHomePlayers,
            [updatedMatch.awayTeam]: locationSwapped ? originalHomePlayers : originalAwayPlayers,
        };

        // Update goal events based on score changes (operates on the cloned events)
        updatedMatch.matchEvents = updateScoreEvents(
            { ...matchPhone, matchEvents: updatedMatch.matchEvents },
            matchLocation,
            data
        );

        try {
            const savedMatch = await updateMatch(updatedMatch);
            if (savedMatch && profile) {
                await recalculateStats(savedMatch, profile);
            }
            router.push(
                `${routes.match}/${matchPhone._id}/overview`,
                "back"
            );
        } catch (error) {
            console.error("Failed to save match:", error);
            presentToast({
                message: t("general.errorSaving"),
                duration: 3000,
                color: "danger",
                position: "bottom",
            });
        }
    };

    const myTeamScore = watch("myTeamScore");
    const opponentScore = watch("opponentScore");

    return (
        <IonPage>
            <HeaderComponent
                showBackButton
                defaultHref={matchPhone ? `${routes.match}/${matchPhone._id}/overview` : routes.tabResults}
                title={t("general.matchDetails")}
                titleTestId="edit-match-details-title"
            />
            <IonContent className="edit-match-content">
                <IonLoading isOpen={loading} />
                <FormProvider {...formMethods}>
                    <form onSubmit={handleSubmit(onSubmit)}>
                        {/* Team Score Header */}
                        <IonCard className="team-score-header">
                            <div className="team-score-row">
                                <IonIcon
                                    icon={myTeamColor ? shirt : shirtOutline}
                                    className="jersey-icon clickable"
                                    style={myTeamColor ? { color: myTeamColor } : undefined}
                                    onClick={() => openColorPicker(MatchLocation.home)}
                                    data-testid="myTeamColor"
                                />
                                <div className="team-info">
                                    <Controller
                                        name="myTeamName"
                                        control={formMethods.control}
                                        render={({ field }) => (
                                            <IonInput
                                                className="team-name-input"
                                                value={field.value}
                                                onIonInput={(e) => field.onChange(e.detail.value)}
                                                label={t("editMatch.myTeam")}
                                                labelPlacement="floating"
                                                data-testid="myTeamNameInput"
                                            />
                                        )}
                                    />
                                </div>
                                <ScoreCounter
                                    value={myTeamScore}
                                    onChange={(val) => setValue("myTeamScore", val)}
                                    testId="myTeamScore"
                                />
                            </div>
                            <div className="team-score-row">
                                <IonIcon
                                    icon={opponentColor ? shirt : shirtOutline}
                                    className="jersey-icon clickable"
                                    style={opponentColor ? { color: opponentColor } : undefined}
                                    onClick={() => openColorPicker(MatchLocation.away)}
                                    data-testid="opponentColor"
                                />
                                <div className="team-info">
                                    <Controller
                                        name="opponentName"
                                        control={formMethods.control}
                                        render={({ field }) => (
                                            <IonInput
                                                className="team-name-input"
                                                value={field.value}
                                                onIonInput={(e) => field.onChange(e.detail.value)}
                                                label={t("editMatch.opponent")}
                                                labelPlacement="floating"
                                                data-testid="opponentNameInput"
                                            />
                                        )}
                                    />
                                </div>
                                <ScoreCounter
                                    value={opponentScore}
                                    onChange={(val) => setValue("opponentScore", val)}
                                    testId="opponentScore"
                                />
                            </div>
                        </IonCard>

                        {/* Match Info Section */}
                        <IonCard className="edit-match-card">
                            <div className="location-row">
                                <IonIcon
                                    icon={locationPitchIcon}
                                    className="location-icon"
                                />
                                <span className="location-label">
                                    {t("editMatch.location")}
                                </span>
                                <div className="location-segment">
                                    <IonFormSegment name="location" value={location}>
                                        <IonSegmentButton value="home">
                                            <IonLabel>{t("general.home")}</IonLabel>
                                        </IonSegmentButton>
                                        <IonSegmentButton value="away">
                                            <IonLabel>{t("general.away")}</IonLabel>
                                        </IonSegmentButton>
                                    </IonFormSegment>
                                </div>
                            </div>

                            {/* Venue Row */}
                            <div className="field-row">
                                <IonIcon
                                    icon={venueStadiumIcon}
                                    className="field-icon"
                                />
                                <div className="field-info">
                                    <Controller
                                        name="venue"
                                        control={formMethods.control}
                                        render={({ field }) => (
                                            <IonInput
                                                className="field-input"
                                                value={field.value}
                                                onIonInput={(e) => field.onChange(e.detail.value)}
                                                label={t("fixture.FixtureNew.venue")}
                                                labelPlacement="floating"
                                                data-testid="venueInput"
                                            />
                                        )}
                                    />
                                </div>
                            </div>

                            {/* Competition Row */}
                            <div className="field-row">
                                <IonIcon
                                    icon={competitionTrophyIcon}
                                    className="field-icon"
                                />
                                <div className="field-info">
                                    <Controller
                                        name="competition"
                                        control={formMethods.control}
                                        render={({ field }) => (
                                            <IonInput
                                                className="field-input"
                                                value={field.value}
                                                onIonInput={(e) => field.onChange(e.detail.value)}
                                                label={t("fixture.FixtureNew.competition")}
                                                labelPlacement="floating"
                                                data-testid="competitionInput"
                                            />
                                        )}
                                    />
                                </div>
                            </div>

                            {/* Position Row */}
                            <div className="field-row">
                                <IonIcon
                                    icon={positionPlayerIcon}
                                    className="field-icon"
                                />
                                <div className="field-info">
                                    <PositionDropdown
                                        name="playerPosition"
                                        label={t("editMatch.position")}
                                        labelPlacement="stacked"
                                        testId="playerPositionSelect"
                                        className="field-dropdown"
                                        slot=""
                                    />
                                </div>
                            </div>
                        </IonCard>

                        <input type="submit" hidden />
                    </form>

                    {/* Save Button */}
                    <div className="save-button-container">
                        <IonButton
                            expand="block"
                            className="save-button"
                            onClick={() => handleSubmit(onSubmit)()}
                            data-testid="saveMatchBtn"
                        >
                            {t("general.save")}
                        </IonButton>
                    </div>
                </FormProvider>

                {/* Space for keyboard */}
                <div style={{ height: "100px" }}></div>

                {/* Color Picker Modal */}
                <IonModal
                    isOpen={showColorPicker}
                    onDidDismiss={() => setShowColorPicker(false)}
                    className="color-picker-modal"
                >
                    <div className="color-picker-modal-content">
                        <div className="color-picker-modal-header">
                            <span className="color-picker-modal-title">{t("editMatch.teamColour")}</span>
                            <IonButton fill="clear" onClick={() => setShowColorPicker(false)}>
                                <IonIcon icon={closeOutline} />
                            </IonButton>
                        </div>
                        <ColorPickerComponent
                            colorPicked={handleColorPicked}
                            selectedColor={colorPickerTeam === MatchLocation.home ? myTeamColor : opponentColor}
                        />
                    </div>
                </IonModal>
            </IonContent>
        </IonPage>
    );
};

export default EditMatchDetails;
